import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { LeaderboardService } from '../services/leaderboardService';

const LeaderboardDisplay = ({ 
  gameMode, 
  difficulty, 
  title, 
  maxEntries = 10, 
  showSeeMore = true,
  currentUsername = null,
  showBadge = false 
}) => {
  const { t } = useLanguage();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [fullLeaderboard, setFullLeaderboard] = useState([]);

  const gameModeKey = LeaderboardService.getGameModeKey(gameMode, difficulty);

  useEffect(() => {
    loadLeaderboard();
  }, [gameModeKey]);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await LeaderboardService.getLeaderboard(gameModeKey);
      setLeaderboard(data.entries || []);
      setFullLeaderboard(data.entries || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSeeMore = async () => {
    if (!expanded) {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  };

  const getPositionDisplay = (position) => {
    if (position === 1) return '🏆';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return `#${position}`;
  };

  const renderBadge = () => {
    if (!showBadge) return null;
    
    const modeText = gameMode === 'plate' ? t('plateMode') : t('codeMode');
    const diffText = t(difficulty);
    
    return (
      <span className={`difficulty-badge ${difficulty}`}>
        {modeText} - {diffText}
      </span>
    );
  };

  const displayedEntries = expanded ? fullLeaderboard : leaderboard.slice(0, maxEntries);
  const hasMoreEntries = leaderboard.length > maxEntries;

  if (loading) {
    return (
      <div className="leaderboard-section">
        {title && <h4 className="leaderboard-title">{title}</h4>}
        <div className="loading-leaderboard">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-section">
        {title && <h4 className="leaderboard-title">{title}</h4>}
        <div className="error-leaderboard">
          <p>{error}</p>
          <button onClick={loadLeaderboard} className="retry-btn-small">
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-section">
      {showBadge ? (
        <div className="leaderboard-title-badge">
          {renderBadge()}
        </div>
      ) : (
        title && <h4 className="leaderboard-title">{title}</h4>
      )}
      
      <div className="leaderboard-content-display">
        {leaderboard.length === 0 ? (
          <div className="empty-leaderboard-display">{t('noScoresYet')}</div>
        ) : (
          <>
            <div className="leaderboard-table-display">
              <div className="leaderboard-header-row-display">
                <span>{t('rank')}</span>
                <span>{t('player')}</span>
                <span>{t('score')}</span>
                <span>{t('time')}</span>
              </div>
              
              {displayedEntries.map((entry, index) => (
                <div 
                  key={`${entry.username}-${entry.timestamp}`}
                  className={`leaderboard-row-display ${
                    currentUsername && entry.username === currentUsername ? 'current-player-display' : ''
                  }`}
                >
                  <span className="rank-display">{getPositionDisplay(index + 1)}</span>
                  <span className="player-display">{entry.username}</span>
                  <span className="score-display">{entry.score}/47</span>
                  <span className="time-display">{entry.timeFormatted}</span>
                </div>
              ))}
            </div>
            
            {showSeeMore && hasMoreEntries && (
              <button 
                onClick={handleSeeMore} 
                className="see-more-btn"
              >
                {expanded ? t('seeLess') : t('seeMore')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardDisplay;